import { UserManager, UserManagerSettings, User } from 'oidc-client-ts'

const ISSUER = import.meta.env.VITE_OIDC_ISSUER as string
const CLIENT_ID = import.meta.env.VITE_OIDC_CLIENT_ID as string
// The plustag oauth-token edge fn requires both client_id and client_secret; it doesn't
// yet support PKCE-only public clients. So we ship the secret in the SPA (public by nature).
// Rotate this + tighten edge fn to accept token_endpoint_auth_method=none as a follow-up.
const CLIENT_SECRET = import.meta.env.VITE_OIDC_CLIENT_SECRET as string

// oauth-preprod.plustag.ai serves BOTH the React /oauth/authorize + /oauth/consent UI
// and (via CloudFront path routing) the /token, /userinfo, /jwks backend endpoints.
// The id_token iss claim is the ISSUER — JWT verification aligns.
const AUTH_UI_ORIGIN = ISSUER

const settings: UserManagerSettings = {
  authority: ISSUER,
  client_id: CLIENT_ID,
  client_secret: CLIENT_SECRET,
  redirect_uri: `${window.location.origin}/oauth/callback`,
  post_logout_redirect_uri: window.location.origin,
  response_type: 'code',
  scope: 'openid profile email',
  loadUserInfo: true,
  automaticSilentRenew: false,          // no silent-renew iframe against split-origin IdP
  monitorSession: false,
  metadata: {
    issuer: ISSUER,
    authorization_endpoint: `${AUTH_UI_ORIGIN}/oauth/authorize`,
    token_endpoint:         `${ISSUER}/oauth/token`,
    userinfo_endpoint:      `${ISSUER}/oauth/userinfo`,
    jwks_uri:               `${ISSUER}/.well-known/jwks.json`,
    revocation_endpoint:    `${ISSUER}/oauth/revoke`,
    end_session_endpoint:   `${AUTH_UI_ORIGIN}/`,
    response_types_supported: ['code'],
    grant_types_supported: ['authorization_code', 'refresh_token'],
    subject_types_supported: ['public'],
    id_token_signing_alg_values_supported: ['RS256'],
    scopes_supported: ['openid', 'email', 'profile'],
    code_challenge_methods_supported: ['S256'],
    token_endpoint_auth_methods_supported: ['client_secret_post', 'client_secret_basic', 'none'],
  },
}

export const userManager = new UserManager(settings)

export async function signIn() {
  await userManager.signinRedirect()
}

export async function completeSignIn(): Promise<User> {
  return await userManager.signinRedirectCallback()
}

export async function signOut() {
  await userManager.removeUser()
  window.location.href = '/'
}

export async function getUser(): Promise<User | null> {
  return await userManager.getUser()
}

export async function getAccessToken(): Promise<string | null> {
  // PlusTag's oauth-token returns an opaque UUID-pair as access_token and a
  // signed JWT as id_token. Our API verifies JWTs, so we send the id_token.
  const user = await userManager.getUser()
  return user?.id_token || null
}
